/**
 * The dashboard section registry (spec §5.8, D-TBD-9; replaces `registerMenuTab` in W3). The eight top-level tabs
 * are fixed and ordered (number = index + 1) and are not registrable, so the numbers 1–8 never move; features
 * register **sections** into them. Overview is composed by the shell and takes no sections.
 *
 * `openDashboard` / `closeDashboard` are the feature-facing entry points; the dashboard feature installs the host
 * that serves them (`setDashboardHost`). Pure apart from that one module-level slot and the section list.
 */
import type { ComponentType } from 'react'
import type { Bindings } from '../../data/schemas'
import type { DashNavPort } from './focus'

export const DASH_TABS = ['overview', 'ship', 'jobs', 'fleet', 'nav', 'intel', 'ledger', 'log'] as const
export type DashTabId = (typeof DASH_TABS)[number]

/** Phase C station groups (spec §3). */
export const STATION_GROUPS = ['trade', 'shipworks', 'offices', 'jobsCrew'] as const
export type StationGroupId = (typeof STATION_GROUPS)[number]

/** A keyboard binding action (`bindings.json keyboard`), e.g. 'wirePanel'. */
export type BindingAction = keyof Bindings['keyboard'] & string

/** What a section may ask about the game when the shell polls it. Read-only. */
export interface DashContext {
  readonly mode: string
  /** Docked at a station (Phase C sections show only then). */
  readonly docked: boolean
  /** A career runs in the Reach (most content needs it). */
  readonly career: boolean
}

/** Where `openDashboard` lands. */
export interface DashTarget {
  readonly tab?: DashTabId
  /** A section id ('fleet.captains'). */
  readonly section?: string
  /** A focus key inside the section, handed to its component. */
  readonly focus?: string
}

/** The commands a section component may send back to the shell. */
export interface DashCommandPort {
  open(at: DashTarget): void
  close(relock: boolean): void
}

export interface DashSectionProps {
  readonly ctx: DashContext
  readonly focus: string | undefined
  readonly port: DashCommandPort
  /** Focus inputs from the shell (arrows/WASD, Enter, F, R, PgUp/PgDn); use `useDashFocus`. */
  readonly nav: DashNavPort
}

export type DashSectionComponent = ComponentType<DashSectionProps>

export interface DashSection {
  readonly id: string
  readonly tab: DashTabId | StationGroupId
  /** Order within the tab (ascending). */
  readonly order: number
  /** From the feature's JSON, never inline. */
  readonly label: string
  /** The binding action that opens the dashboard here ('wirePanel' → N). */
  readonly deepLink?: BindingAction
  /** Phase C station sections: hidden in flight. */
  readonly dockedOnly?: boolean
  /** False → the section shows its empty state; it is never hidden. */
  available(ctx: DashContext): boolean
  /** Polled ≤ 1 Hz while open; 0 hides the badge. */
  badge?(ctx: DashContext): number
  readonly component: DashSectionComponent
}

const sections: DashSection[] = []
const listeners = new Set<() => void>()

function changed(): void {
  for (const fn of listeners) fn()
}

export function isDashTab(id: string): id is DashTabId {
  return (DASH_TABS as readonly string[]).includes(id)
}

function isStationGroup(id: string): id is StationGroupId {
  return (STATION_GROUPS as readonly string[]).includes(id)
}

/** Registers a section; returns the unregister. Throws on a bad tab, a duplicate id or a section in Overview. */
export function registerDashSection(s: DashSection): () => void {
  if (!isDashTab(s.tab) && !isStationGroup(s.tab)) throw new Error(`Dashboard section "${s.id}": unknown tab "${s.tab}"`)
  if (s.tab === 'overview') throw new Error(`Dashboard section "${s.id}": Overview is composed by the shell`)
  if (sections.some((o) => o.id === s.id)) throw new Error(`Dashboard section "${s.id}" is already registered`)
  sections.push(s)
  sections.sort((a, b) => a.order - b.order || (a.id < b.id ? -1 : a.id > b.id ? 1 : 0))
  changed()
  return (): void => {
    const i = sections.indexOf(s)
    if (i < 0) return
    sections.splice(i, 1)
    changed()
  }
}

/** The sections of one tab in order; `docked` false drops the docked-only ones. */
export function dashSections(tab: DashTabId | StationGroupId, docked: boolean): DashSection[] {
  return sections.filter((s) => s.tab === tab && (docked || s.dockedOnly !== true))
}

/** Every registered section (tests, the deep-link table). */
export function allDashSections(): readonly DashSection[] {
  return sections
}

/** Called when the section list changes; returns the unsubscribe. */
export function onDashSections(fn: () => void): () => void {
  listeners.add(fn)
  return (): void => {
    listeners.delete(fn)
  }
}

/** Problems with the registered sections against the binding actions (empty when sound). */
export function validateDashSections(actions: readonly string[]): string[] {
  const problems: string[] = []
  const seen = new Set<string>()
  for (const s of sections) {
    if (seen.has(s.id)) problems.push(`duplicate section id "${s.id}"`)
    seen.add(s.id)
    if (!isDashTab(s.tab) && !isStationGroup(s.tab)) problems.push(`"${s.id}": unknown tab "${s.tab}"`)
    if (s.tab === 'overview') problems.push(`"${s.id}": sections may not go into overview`)
    if (s.deepLink !== undefined && !actions.includes(s.deepLink)) problems.push(`"${s.id}": deep link "${s.deepLink}" is not a binding action`)
  }
  return problems
}

/** The feature that serves `openDashboard` / `closeDashboard`. */
export interface DashboardHost {
  open(at?: DashTarget): void
  close(relock: boolean): void
  readonly isOpen: boolean
}

let host: DashboardHost | null = null

/** Installs the host (the dashboard feature's init); returns the uninstall. */
export function setDashboardHost(h: DashboardHost): () => void {
  host = h
  return (): void => {
    if (host === h) host = null
  }
}

/** Opens the dashboard (at a tab, section and focus). A no-op before the dashboard feature is up. */
export function openDashboard(at?: DashTarget): void {
  host?.open(at)
}

/** Closes the dashboard; `relock` true only from inside a user gesture (spec §1.5). */
export function closeDashboard(relock: boolean): void {
  host?.close(relock)
}

export function dashboardOpen(): boolean {
  return host?.isOpen ?? false
}

/** Tests only: drops every section and the host. */
export function resetDashRegistry(): void {
  sections.length = 0
  host = null
  changed()
}
